import { Avatar } from 'components/Avatar';
import { Button } from 'components/Button';
import { Card } from 'components/Card';
import { CardRow } from 'components/CardRow';
import { CloudThumbnail } from 'components/Thumbnail/CloudThumbnail';
import { Icon, ICONS } from 'components/Icon';
import { Pill } from 'components/Pill';
import { Txt } from 'components/Txt';
import { formatDateTimeAgo } from 'lib/riskbase';
import { theme } from 'lib/theme';
import React from 'react';
import { StyleSheet, View } from 'react-native';
import { useDispatch } from 'react-redux';
import {
  formatFileSize,
  getFileTypeFromUntrustedExtension,
} from 'service/file';
import { FILE_TYPES } from 'service/file/types';
import { doFileShareDirect, doSetImagePreview } from 'store/actions';
import type { TaskDetailsResponse } from 'types/Tasks';
import { getStatusIconColor } from './utilities';

type TaskAssignedCommentFile =
  TaskDetailsResponse['comments'][number]['files'][number];

type TaskStatusChange = NonNullable<
  TaskDetailsResponse['comments'][number]['statusChange']
>;

const getTaskCommentFileType = (file: TaskAssignedCommentFile) =>
  (file.fileExtension
    ? getFileTypeFromUntrustedExtension(file.fileExtension)
    : undefined) ?? {
    ...FILE_TYPES.file,
    mime: file.mimeType,
  };

const getTaskCommentFileDownloadTitle = (file: TaskAssignedCommentFile) => {
  const extension = file.fileExtension?.toLowerCase();
  const suffix = extension ? `.${extension}` : '';
  return suffix && file.filename.toLowerCase().endsWith(suffix)
    ? file.filename.slice(0, -suffix.length)
    : file.filename;
};

const TaskAssignedCommentFileItem: React.FC<{
  file: TaskAssignedCommentFile;
}> = ({ file }) => {
  const dispatch = useDispatch();

  const handleDownload = React.useCallback(() => {
    dispatch(
      doFileShareDirect({
        url: file.fullUrl,
        title: getTaskCommentFileDownloadTitle(file),
        fileType: getTaskCommentFileType(file),
      }),
    );
  }, [dispatch, file]);

  return (
    <View style={styles.fileContainer}>
      <View style={styles.fileThumbnail}>
        {file.smallUrl ? (
          <CloudThumbnail
            imageUrl={file.smallUrl}
            width={40}
            height={40}
            borderRadius={theme.cardBorderRadius}
            contentFit="cover"
            retryOnError
          />
        ) : (
          <Icon
            source={
              file.mimeType.startsWith('image/')
                ? ICONS.ImageThin
                : ICONS.PaperClipLight
            }
            color={theme.colors.white}
          />
        )}
      </View>
      <View style={styles.fileDetailsRow}>
        <Txt size="xsmall" numberOfLines={2} style={{ flex: 1 }}>
          {file.filename}
        </Txt>
        <Txt
          style={{ color: theme.colors.textLight }}
          size="xsmall"
          numberOfLines={1}
        >
          {formatFileSize(file.bytes)}
        </Txt>
      </View>
      <Button
        icon={ICONS.ArrowDownLight}
        iconColor={theme.colors.blue}
        onPress={handleDownload}
        accessibilityLabel={`Download ${file.filename}`}
      />
    </View>
  );
};

export const TaskAssignedComment: React.FC<{
  authorName: string;
  authorAvatarUrl: string | null;
  comment: string;
  createdAt: Date;
  files: TaskAssignedCommentFile[];
  statusChange: TaskStatusChange | null;
}> = ({
  authorName,
  authorAvatarUrl,
  comment,
  createdAt,
  files,
  statusChange,
}) => {
  const dispatch = useDispatch();
  const hasComment = comment.trim().length > 0;

  const handlePreview = React.useCallback(
    (file: TaskAssignedCommentFile) => {
      if (file.mimeType.startsWith('image/') && file.largeUrl) {
        dispatch(doSetImagePreview({ image: file.largeUrl }));
      }
    },
    [dispatch],
  );

  return (
    <Card
      bodyStyle={{
        backgroundColor: theme.colors.white,
      }}
      separator={null}
    >
      <CardRow
        childrenStyle={{
          flexDirection: 'row',
          gap: theme.padding,
        }}
      >
        <Avatar
          icon={ICONS.CircleUserCustom}
          iconColor={'#bbbbbb'}
          iconPadding={0}
          data={authorAvatarUrl ? { remoteUrl: authorAvatarUrl } : null}
          size={40}
        />
        <View style={{ flex: 1 }}>
          <View style={{ flexDirection: 'row', flex: 1 }}>
            <Txt size="xxxsmall">
              <Txt weight="medium">{authorName}</Txt>
              {'  '}
              <Txt
                size="xxsmall"
                style={{ color: theme.colors.fieldValueText }}
              >
                {formatDateTimeAgo(createdAt)}
              </Txt>
            </Txt>
          </View>
          {hasComment && <Txt size="xsmall">{comment}</Txt>}
          {statusChange && (
            <View
              style={{
                marginTop: theme.padding / 2,
              }}
            >
              <View
                style={{
                  alignItems: 'center',
                  flexDirection: 'row',
                  flexWrap: 'wrap',
                  gap: theme.padding / 2,
                }}
              >
                <Pill
                  withIcon
                  iconColor={getStatusIconColor(statusChange.from.statusType)}
                  label={statusChange.from.statusName}
                  textSize="xxxsmall"
                  textWeight="bold"
                />
                <Icon
                  source={ICONS.ArrowRightLight}
                  color={theme.colors.black80}
                  size={14}
                />
                <Pill
                  withIcon
                  iconColor={getStatusIconColor(statusChange.to.statusType)}
                  label={statusChange.to.statusName}
                  textSize="xxxsmall"
                  textWeight="bold"
                />
              </View>
            </View>
          )}
        </View>
      </CardRow>

      {!!files.length && (
        <Card style={{ marginBottom: theme.padding }}>
          {files.map((file) => (
            <CardRow
              style={{
                backgroundColor: theme.colors.black2,
              }}
              key={file.id}
              onPress={
                file.mimeType.startsWith('image/') && file.largeUrl
                  ? () => handlePreview(file)
                  : undefined
              }
              accessibilityLabel={
                file.mimeType.startsWith('image/') && file.largeUrl
                  ? `Preview ${file.filename}`
                  : undefined
              }
            >
              <TaskAssignedCommentFileItem file={file} />
            </CardRow>
          ))}
        </Card>
      )}
    </Card>
  );
};

const styles = StyleSheet.create({
  fileContainer: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: theme.padding / 2,
  },
  fileThumbnail: {
    alignItems: 'center',
    backgroundColor: theme.colors.black30,
    borderRadius: theme.cardBorderRadius,
    height: 40,
    justifyContent: 'center',
    marginRight: theme.padding / 2,
    overflow: 'hidden',
    width: 40,
  },
  fileDetailsRow: {
    alignItems: 'center',
    flex: 1,
    flexDirection: 'row',
    gap: theme.padding / 2,
  },
});
